/**
 * Lead detail, below the timeline: the quote and the job (contractor pages
 * phase 3).
 *
 *  - a project exists → ProjectCard (Start / Mark complete / Cancel);
 *  - otherwise the latest quote with its status and next step (draft: edit or
 *    send; sent: withdraw; declined / expired / withdrawn: a revised quote);
 *  - and "Create a quote" when the API says one can be sent, or the reason in
 *    plain English when it cannot (`blocked_reason`).
 *
 * The `quotes` capability gates every action; without it the card is read
 * only. An organisation without the quotes feature (whoami `features.quotes`
 * false, or a server that answers 404) sees nothing here at all.
 */
import { useState } from 'react';
import type { LeadDetail, LeadQuoteSummary, LeadQuotes, QuoteBlockedReason } from '../api/types';
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import { Link } from '../router';
import { ErrorNotice, Loading, formatDate, useToast } from '../ui/kit';
import { useMutation, useQuery } from '../ui/hooks';
import { formatBps, formatCents, lineItemTotalCents, netCents, successFeeCents } from './feeMath';
import { ProjectCard } from './ProjectCard';
import { BLOCKED_COPY, QuoteComposer, QuoteError, SendQuoteDialog } from './QuoteComposer';

export interface QuoteSlotProps {
  lead: LeadDetail;
  /** Called after any quote or project change, so the detail refetches. */
  onChanged: () => void;
}

const QUOTE_STATUS_LABEL: Record<LeadQuoteSummary['status'], string> = {
  draft: 'Draft',
  sent: 'Sent',
  accepted: 'Accepted',
  declined: 'Declined',
  expired: 'Expired',
  withdrawn: 'Withdrawn',
};

const QUOTE_STATUS_TONE: Record<LeadQuoteSummary['status'], string> = {
  draft: '',
  sent: 'info',
  accepted: 'ok',
  declined: 'bad',
  expired: 'warn',
  withdrawn: '',
};

const REVISABLE = new Set<LeadQuoteSummary['status']>(['declined', 'expired', 'withdrawn']);

type Mode = { kind: 'view' } | { kind: 'new'; prefill: LeadQuoteSummary | null } | { kind: 'edit'; draft: LeadQuoteSummary };

function latestOf(items: LeadQuoteSummary[]): LeadQuoteSummary | null {
  return items.reduce<LeadQuoteSummary | null>((best, q) => (!best || q.version > best.version ? q : best), null);
}

export function QuoteSlot({ lead, onChanged }: QuoteSlotProps) {
  const { api, role, whoami } = useSession();
  const off = whoami?.features ? !whoami.features.quotes : false;
  const quotes = useQuery(() => api.listLeadQuotes(lead.lead_id), [lead.lead_id, lead.status, lead.contact_locked ?? null], { enabled: !off });
  const [mode, setMode] = useState<Mode>({ kind: 'view' });
  const toast = useToast();
  const canAct = can(role, 'quotes');

  if (off) return null;
  // An API without the quotes routes (or with the feature switched off) answers 404: nothing to show.
  if (quotes.error?.status === 404) return null;

  const changed = () => {
    setMode({ kind: 'view' });
    void quotes.reload();
    onChanged();
  };

  const data = quotes.data;
  const project = data?.project ?? null;
  const latest = data ? latestOf(data.items) : null;
  const title = project ? 'Job' : 'Quote';

  return (
    <section className="panel" aria-labelledby="quote-slot-title">
      <div className="panel-head">
        <h2 id="quote-slot-title">{title}</h2>
      </div>
      <div className="panel-body stack">
        {quotes.loading && !data ? (
          <Loading label="Loading quotes" />
        ) : quotes.error || !data ? (
          <ErrorNotice error={quotes.error} title="Could not load quotes" />
        ) : mode.kind === 'new' ? (
          <QuoteComposer
            leadId={lead.lead_id}
            feeBps={data.fee_bps}
            prefill={mode.prefill}
            onCancel={() => setMode({ kind: 'view' })}
            onDone={(_q, sent) => {
              toast(sent ? 'Quote sent to the homeowner' : 'Draft saved');
              changed();
            }}
          />
        ) : mode.kind === 'edit' ? (
          <QuoteComposer
            leadId={lead.lead_id}
            feeBps={data.fee_bps}
            draft={mode.draft}
            onCancel={() => setMode({ kind: 'view' })}
            onDone={(_q, sent) => {
              toast(sent ? 'Quote sent to the homeowner' : 'Draft saved');
              changed();
            }}
          />
        ) : project ? (
          <ProjectCard
            project={project}
            quote={data.items.find((q) => q.id === project.quote_id) ?? latest}
            canAct={canAct}
            onChanged={changed}
          />
        ) : (
          <>
            {latest ? <QuoteCard quote={latest} feeBps={data.fee_bps} canAct={canAct} onChanged={changed} onEdit={() => setMode({ kind: 'edit', draft: latest })} /> : null}
            <NextQuote
              data={data}
              latest={latest}
              canAct={canAct}
              onCreate={() => setMode({ kind: 'new', prefill: latest && REVISABLE.has(latest.status) ? latest : null })}
            />
          </>
        )}
      </div>
    </section>
  );
}

/** "Create a quote" / "Send a revised quote", or why it isn't possible. */
function NextQuote({ data, latest, canAct, onCreate }: { data: LeadQuotes; latest: LeadQuoteSummary | null; canAct: boolean; onCreate: () => void }) {
  const { role } = useSession();
  // An open quote (draft / sent / accepted) carries its own next step.
  if (latest && !REVISABLE.has(latest.status)) return null;
  if (data.can_quote) {
    if (!canAct) return latest ? null : <p className="muted" style={{ margin: 0 }}>No quote yet.</p>;
    return (
      <div className="stack-sm">
        {latest ? null : (
          <p className="muted" style={{ margin: 0 }}>
            Send the homeowner a price they can accept online. FinderlyFix charges a {formatBps(data.fee_bps)} success fee only once they accept and the job is
            confirmed complete.
          </p>
        )}
        <div className="row">
          <button type="button" className="btn btn-primary" onClick={onCreate}>
            {latest ? 'Send a revised quote' : 'Create a quote'}
          </button>
        </div>
      </div>
    );
  }
  return <BlockedReason reason={data.blocked_reason} canAcceptSchedule={can(role, 'acceptFeeSchedule')} />;
}

function BlockedReason({ reason, canAcceptSchedule }: { reason: QuoteBlockedReason | null; canAcceptSchedule: boolean }) {
  if (!reason || reason === 'PROJECT_EXISTS' || reason === 'QUOTE_OPEN_EXISTS') {
    return reason ? <p className="muted" style={{ margin: 0 }}>{BLOCKED_COPY[reason]}</p> : null;
  }
  if (reason === 'FEE_SCHEDULE_REQUIRED') {
    return (
      <div className="notice warn" data-reason={reason}>
        <span>
          {BLOCKED_COPY.FEE_SCHEDULE_REQUIRED}{' '}
          {canAcceptSchedule ? (
            <Link to="/billing#fee-schedule">Review and accept the fee schedule</Link>
          ) : (
            <>
              Ask an owner or admin to accept it on <Link to="/billing#fee-schedule">Billing</Link>.
            </>
          )}
        </span>
      </div>
    );
  }
  const tone = reason === 'QUOTES_DISABLED' || reason === 'BILLING_NOT_ENABLED' ? 'info' : 'warn';
  return (
    <div className={`notice ${tone}`} data-reason={reason}>
      <span>{BLOCKED_COPY[reason]}</span>
    </div>
  );
}

function QuoteCard({
  quote,
  feeBps,
  canAct,
  onChanged,
  onEdit,
}: {
  quote: LeadQuoteSummary;
  feeBps: number;
  canAct: boolean;
  onChanged: () => void;
  onEdit: () => void;
}) {
  const { api } = useSession();
  const toast = useToast();
  const [confirmSend, setConfirmSend] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [reason, setReason] = useState('');
  const bps = quote.success_fee_bps ?? feeBps;
  const fee = successFeeCents(quote.amount_cents, bps);
  const send = useMutation(async () => {
    await api.sendQuote(quote.id);
    toast('Quote sent to the homeowner');
    onChanged();
  });
  const withdraw = useMutation(async () => {
    await api.withdrawQuote(quote.id, reason.trim() || undefined);
    setWithdrawing(false);
    toast('Quote withdrawn');
    onChanged();
  });
  const lines = quote.line_items ?? [];

  return (
    <div className="stack" data-quote-status={quote.status}>
      <div className="row-between">
        <div className="row">
          <strong>Quote v{quote.version}</strong>
          <span className={`tag ${QUOTE_STATUS_TONE[quote.status]}`.trim()}>{QUOTE_STATUS_LABEL[quote.status]}</span>
        </div>
        <strong style={{ fontSize: 18 }}>{formatCents(quote.amount_cents)}</strong>
      </div>
      <p className="small muted" style={{ margin: 0 }}>
        FinderlyFix success fee ({formatBps(bps)}): {formatCents(fee)} · You keep: {formatCents(netCents(quote.amount_cents, bps))}
        {quote.status === 'draft' ? '' : ' · charged only once the job is confirmed complete'}
      </p>
      <dl className="facts">
        {quote.sent_at ? (
          <>
            <dt>Sent</dt>
            <dd>{formatDate(quote.sent_at)}</dd>
          </>
        ) : null}
        {quote.status === 'sent' && quote.valid_until ? (
          <>
            <dt>Valid until</dt>
            <dd>{formatDate(quote.valid_until)}</dd>
          </>
        ) : null}
        {quote.status === 'declined' ? (
          <>
            <dt>Declined</dt>
            <dd>
              {formatDate(quote.declined_at)}
              {quote.decline_reason ? ` · “${quote.decline_reason}”` : ''}
            </dd>
          </>
        ) : null}
        {quote.status === 'expired' && quote.valid_until ? (
          <>
            <dt>Expired</dt>
            <dd>{formatDate(quote.valid_until)}</dd>
          </>
        ) : null}
        {quote.status === 'withdrawn' ? (
          <>
            <dt>Withdrawn</dt>
            <dd>{formatDate(quote.withdrawn_at)}</dd>
          </>
        ) : null}
        {quote.status === 'accepted' ? (
          <>
            <dt>Accepted</dt>
            <dd>{formatDate(quote.accepted_at)}</dd>
          </>
        ) : null}
      </dl>
      <div className="sunk">
        <strong>What the job covers</strong>
        <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{quote.scope}</p>
      </div>
      {lines.length ? (
        <div className="table-wrap">
          <table className="data">
            <caption className="visually-hidden">Line items</caption>
            <thead>
              <tr>
                <th scope="col">Item</th>
                <th scope="col" className="num">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.map((item, i) => {
                const view = item as { description: string; qty?: number; unit_cents?: number };
                return (
                  <tr key={i}>
                    <td>
                      {view.description}
                      {typeof view.qty === 'number' && typeof view.unit_cents === 'number' && view.qty !== 1 ? (
                        <span className="small muted">
                          {' '}
                          · {view.qty} × {formatCents(view.unit_cents)}
                        </span>
                      ) : null}
                    </td>
                    <td className="num">{formatCents(lineItemTotalCents(item))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {quote.terms ? (
        <div>
          <strong className="small">Terms</strong>
          <p className="small" style={{ margin: '2px 0 0', whiteSpace: 'pre-wrap' }}>
            {quote.terms}
          </p>
        </div>
      ) : null}

      {quote.status === 'sent' ? (
        <p className="small muted" style={{ margin: 0 }}>
          The homeowner can accept it from their email{quote.valid_until ? ` until ${formatDate(quote.valid_until)}` : ''}.
        </p>
      ) : null}

      {canAct && quote.status === 'draft' ? (
        <>
          <QuoteError error={send.error} />
          <div className="row">
            <button type="button" className="btn btn-primary" onClick={() => setConfirmSend(true)}>
              Send to the homeowner…
            </button>
            <button type="button" className="btn" onClick={onEdit}>
              Edit draft
            </button>
          </div>
          <SendQuoteDialog
            open={confirmSend}
            amount={quote.amount_cents}
            feeBps={bps}
            pending={send.pending}
            onClose={() => setConfirmSend(false)}
            onConfirm={async () => {
              await send.run();
              setConfirmSend(false);
            }}
          />
        </>
      ) : null}

      {canAct && quote.status === 'sent' ? (
        withdrawing ? (
          <div className="sunk stack-sm">
            <p style={{ margin: 0 }}>The homeowner can no longer accept it. You can send a revised quote afterwards.</p>
            <label className="field">
              <span className="label">
                Why? <span className="faint" style={{ fontWeight: 500 }}>· optional, kept on file</span>
              </span>
              <input className="input" maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} />
            </label>
            <QuoteError error={withdraw.error} />
            <div className="row">
              <button type="button" className="btn btn-danger" disabled={withdraw.pending} onClick={() => void withdraw.run()}>
                {withdraw.pending ? 'Withdrawing…' : 'Withdraw quote'}
              </button>
              <button type="button" className="btn" disabled={withdraw.pending} onClick={() => setWithdrawing(false)}>
                Back
              </button>
            </div>
          </div>
        ) : (
          <div className="row">
            <button type="button" className="btn btn-ghost" onClick={() => setWithdrawing(true)}>
              Withdraw quote…
            </button>
          </div>
        )
      ) : null}
    </div>
  );
}
